import { PageHeaderProvider } from "@/providers/PageHeaderProvider";
import { Campaign } from "@/types/campaign";
import { store } from "@/utils/jotai";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { atom, useAtom } from "jotai";
import { SetStateAction, useMemo, useRef } from "react";

const campaignTriggersAtom = atom<{
  campaignId: number;
  triggers: Campaign["triggers"];
}>();

export const Route = createFileRoute(
  "/_dashboard/campaigns/$id/(edit)/planning/triggers",
)({
  component: RouteComponent,
  staticData: {
    isBreadcrumbRoot: true,
    crumb: "Salgstriggers",
    meta: {
      title: "Salgstriggers",
    },
  },
  beforeLoad: ({ context, params }) => {
    store.set(campaignTriggersAtom, (prev) =>
      prev?.campaignId === params.id
        ? prev
        : {
            campaignId: params.id,
            triggers: context.campaign.triggers,
          },
    );

    const useTriggers = () => {
      const { data: campaign } = context.useCampaignQuery();
      const campaignRef = useRef(campaign);
      campaignRef.current = campaign;

      const triggersAtom = useMemo(
        () =>
          atom(
            (get) => {
              const state = get(campaignTriggersAtom);
              if (state?.campaignId === params.id) return state.triggers;
              return campaignRef.current.triggers;
            },
            (_, set, action: SetStateAction<Campaign["triggers"]>) => {
              set(campaignTriggersAtom, (prev) => {
                if (prev?.campaignId !== params.id) return prev;
                const newTriggers =
                  typeof action === "function" ? action(prev.triggers) : action;
                return newTriggers === prev.triggers
                  ? prev
                  : { ...prev, triggers: newTriggers };
              });
            },
          ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [params.id],
      );

      const [triggers, setTriggers] = useAtom(triggersAtom);

      const isDirty = useMemo(
        () => triggers !== campaign.triggers,
        [campaign.triggers, triggers],
      );

      return {
        triggers,
        setTriggers,
        isDirty,
      };
    };

    return {
      useTriggers,
    };
  },
  onLeave: () => {
    store.set(campaignTriggersAtom, undefined);
  },
});

function RouteComponent() {
  return (
    <PageHeaderProvider options={{ title: "Salgstriggers" }}>
      <Outlet />
    </PageHeaderProvider>
  );
}
